import React from 'react'
import { Box, Chip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import theme from '../../../themeConfig'
import { BRANCH } from './processTree'

/**
 * ProcessTreeInspectorParts.jsx
 * The building blocks of the process Tree inspector and of the operation dialog that mirrors it: the
 * palette, the field, the stat tile, the section title, the cards and the link chips. Transcribed from
 * the MIC Workflow Studio's "Editor del nodo" and painted with `themeConfig.js`, so the Studio's
 * STRUCTURE sits inside Jupiter's palette instead of beside it.
 *
 * Nothing here knows about the model: every part receives what to print and, when it edits, a plain
 * `onChange(value)`. The two surfaces that compose them (`ProcessTreeInspector`, `ProcessTreeJobDialog`)
 * are the ones that decide what is editable and where a change goes.
 */

const DASH = '—'

const P = theme.palette
const T = {
  bgCard: P.background.card,
  bgCardSoft: P.background.color_212121,
  accent: P.primary.main,
  accentBorder: alpha(P.primary.main, 0.32),
  accentSoft: alpha(P.primary.main, 0.12),
  okBg: P.background.table_row_hover,
  warn: P.warning.main,
  warnBg: alpha(P.warning.main, 0.16),
  warnBorder: alpha(P.warning.main, 0.45),
  error: P.error.main,
  textMain: '#f9f9f9',
  textMuted: '#bbbbbb',
  textFaint: '#9a9a9a',
  borderSoft: P.background.color_373636,
  borderFaint: P.background.row2,
  sans: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
}
/** The theme's radii, not the Studio's: `sm` for cards and tiles, MIC's own 4px for fields. */
const R = { card: theme.tokens.radius.sm + 'px', field: '4px', pill: '999px' }

const TABULAR = { fontVariantNumeric: 'tabular-nums' }
const ELLIPSIS = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }

/** The one mark left beside the name: a skipped operation says so (S74), in the theme's warning. */
const SkippedMark = ({ label }) => (
  <Box
    component='span'
    data-testid='inspector-chip-skipped'
    sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', padding: '4px 11px', borderRadius: R.pill, whiteSpace: 'nowrap', border: '1px solid ' + T.warnBorder, background: T.warnBg, color: T.warn }}
  >
    {label}
  </Box>
)

/** Studio section header, with the hairline that fills the rest of the row. */
const SectionTitle = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11.5px', fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: T.textFaint, marginBottom: '12px' }}>
    {label}
    <Box component='span' sx={{ flex: 1, height: '1px', background: T.borderFaint }} />
  </Box>
)

/** Studio stat tile. */
const Tile = ({ testId, label, value, accent = false }) => (
  <Box data-testid={testId} sx={{ background: T.bgCardSoft, border: '1px solid ' + T.borderFaint, borderRadius: R.card, padding: '12px 14px', minWidth: 0 }}>
    <Box sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: T.textFaint, ...ELLIPSIS }}>{label}</Box>
    <Box sx={{ fontSize: '22px', fontWeight: 700, marginTop: '4.8px', ...TABULAR, color: accent ? T.accent : T.textMain, whiteSpace: 'nowrap' }}>{value}</Box>
  </Box>
)

const FIELD_LABEL = { fontSize: '11px', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: T.textFaint }
const FIELD_LABEL_ELLIPSIS = { ...FIELD_LABEL, ...ELLIPSIS }
const FIELD_BOX = { padding: '0 12px', borderRadius: R.field, border: '1px solid ' + T.borderSoft, fontSize: '14px', fontFamily: 'inherit', ...TABULAR }

const SWITCH_BOX = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', border: '1px solid ' + T.borderSoft, borderRadius: R.card, padding: '0 12px', minHeight: '42px', width: '100%' }

const SwitchKnob = ({ on }) => (
  <Box component='span' sx={{ flex: '0 0 34px', width: '34px', height: '18px', borderRadius: R.pill, background: on ? T.okBg : T.bgCard, display: 'flex', alignItems: 'center', padding: '0 2px', justifyContent: on ? 'flex-end' : 'flex-start' }}>
    <Box component='span' sx={{ width: '14px', height: '14px', borderRadius: R.pill, background: on ? T.accent : T.textFaint }} />
  </Box>
)

/**
 * Studio `fld()`: label + a 42px box. Read-only (no `onChange`) it sits on `bg-card` in `text-faint`
 * and is a div: nothing to type into (S83). Editable it is a real control on `bg-card-soft` in
 * `text-main`, the Studio's own editable state. `kind` follows the Studio: 'text', 'number', 'select',
 * 'switch', 'area'.
 *
 * A control can be editable and still `disabled` (the Extern mark of a job that already started, the
 * operators of a subcontracted job): it keeps the editable look, is greyed out and carries `reason` as
 * its title, the same way the graph's panel explains its own blocks through a tooltip (RN24).
 *
 * @param {Object} props
 * @param {{value: string, label: string}[]} [props.options] The choices of a 'select'
 * @param {boolean} [props.clearable] Whether a 'select' offers the empty choice
 * @param {React.ReactNode} [props.action] A control drawn at the right of the label (e.g. "Change technology")
 * @param {boolean} [props.error] Paints the box in the error colour (a required code left empty)
 */
const Field = ({
  testId, label, value, kind = 'text', full = false, on = false, onChange: onChangeProp = null,
  options = [], clearable = false, disabled = false, reason = '', placeholder = '', action = null, error = false, min
}) => {
  const editable = typeof onChangeProp === 'function'
  const onChange = editable ? onChangeProp : null
  const borderColor = error ? T.error : T.borderSoft
  const boxStyle = editable
    ? { ...FIELD_BOX, borderColor, background: T.bgCardSoft, color: disabled ? T.textFaint : T.textMain, outline: 'none', width: '100%', minWidth: 0, opacity: disabled ? 0.7 : 1, cursor: disabled ? 'not-allowed' : undefined, '&:focus': { borderColor: T.accent } }
    : { ...FIELD_BOX, borderColor, background: T.bgCard, color: T.textFaint }

  const renderControl = () => {
    if (kind === 'switch') {
      if (!editable) {
        return (
          <Box data-testid={testId} data-on={on} sx={SWITCH_BOX}>
            <Box component='span' sx={{ fontSize: '14.5px', color: T.textMuted }}>{value}</Box>
            <SwitchKnob on={on} />
          </Box>
        )
      }
      return (
        <Box
          component='button'
          type='button'
          role='switch'
          aria-checked={on}
          aria-label={label}
          data-testid={testId}
          data-on={on}
          disabled={disabled}
          title={reason}
          onClick={() => onChange(!on)}
          sx={{ ...SWITCH_BOX, background: T.bgCardSoft, font: 'inherit', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1, '&:focus-visible': { borderColor: T.accent, outline: 'none' } }}
        >
          <Box component='span' sx={{ fontSize: '14.5px', color: disabled ? T.textFaint : T.textMain }}>{value}</Box>
          <SwitchKnob on={on} />
        </Box>
      )
    }
    if (kind === 'area') {
      return editable
        ? <Box component='textarea' data-testid={testId} value={value} disabled={disabled} title={reason} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} sx={{ ...boxStyle, minHeight: '68px', padding: '10px 12px', lineHeight: 1.45, resize: 'vertical' }} />
        : <Box data-testid={testId} sx={{ ...boxStyle, minHeight: '68px', padding: '10px 12px', lineHeight: 1.45, overflowWrap: 'anywhere' }}>{value}</Box>
    }
    if (kind === 'select' && editable) {
      // The current value is always offered, even when the list does not (yet) hold it: a select that
      // silently showed the first option would be stating a value that is not the model's.
      const known = options.some((option) => option.value === value)
      const choices = !known && value !== '' ? [{ value, label: value }, ...options] : options
      return (
        <Box
          component='select'
          data-testid={testId}
          value={value}
          disabled={disabled}
          title={reason}
          onChange={(event) => onChange(event.target.value)}
          sx={{ ...boxStyle, height: '42px', cursor: disabled ? 'not-allowed' : 'pointer', '& option': { background: T.bgCardSoft, color: T.textMain } }}
        >
          {clearable || value === '' ? <option value=''>{DASH}</option> : null}
          {choices.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Box>
      )
    }
    if (editable) {
      return (
        <Box
          component='input'
          type={kind === 'number' ? 'number' : 'text'}
          min={kind === 'number' ? (min === undefined ? 0 : min) : undefined}
          data-testid={testId}
          value={value}
          disabled={disabled}
          title={reason}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          sx={{ ...boxStyle, height: '42px' }}
        />
      )
    }
    return (
      <Box data-testid={testId} sx={{ ...boxStyle, display: 'flex', alignItems: 'center', gap: '8px', height: '42px' }}>
        <Box component='span' sx={{ flexGrow: 1, minWidth: 0, ...ELLIPSIS }}>{value}</Box>
        {kind === 'select' ? <Box component='span' sx={{ fontSize: '10px', color: T.textFaint }}>▾</Box> : null}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', gridColumn: full ? '1 / -1' : 'auto', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minHeight: '18px' }}>
        <Box component='span' sx={FIELD_LABEL}>{label}</Box>
        {action}
      </Box>
      {renderControl()}
    </Box>
  )
}

/** Studio order badge of a task/operation card. */
const OrderBadge = ({ value }) => (
  <Box component='span' sx={{ flexShrink: 0, minWidth: '32px', height: '28px', padding: '0 8px', borderRadius: R.card, background: T.accentSoft, border: '1px solid ' + T.accentBorder, color: T.accent, fontSize: '13.5px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', ...TABULAR }}>
    {value}
  </Box>
)

/** Studio small chip inside a card. */
const CardChip = ({ label }) => (
  <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '28px', padding: '0 8px', borderRadius: R.card, border: '1px solid ' + T.borderSoft, background: 'transparent', color: T.textMuted, fontSize: '12.5px', fontWeight: 600, maxWidth: '220px', whiteSpace: 'nowrap' }}>
    <Box component='span' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
  </Box>
)

const CARD_SX = { background: T.bgCardSoft, border: '1px solid ' + T.borderFaint, borderRadius: R.card, padding: '12px' }

/** Studio empty card. */
const EmptyCard = ({ testId, text }) => (
  <Box data-testid={testId} sx={{ ...CARD_SX, padding: '24px 12px', textAlign: 'center', fontSize: '14px', color: T.textMuted }}>{text}</Box>
)

/** The bare input a task card edits in place with: no chrome of its own, it borrows the card's. */
const INLINE_INPUT = { border: 0, outline: 'none', background: 'transparent', fontFamily: 'inherit', padding: 0, minWidth: 0, borderBottom: '1px solid ' + T.accentBorder }

/** The Studio's small pill action (the one that opens "Change technology" next to a field label). */
const SMALL_ACTION_SX = { height: '22px', padding: '0 10px', borderRadius: R.pill, border: '1px solid ' + T.borderSoft, background: 'transparent', color: T.textMuted, fontSize: '11px', fontWeight: 700, letterSpacing: '.04em', cursor: 'pointer', fontFamily: 'inherit', '&:hover': { color: T.accent, borderColor: T.accentBorder }, '&:disabled': { opacity: 0.5, cursor: 'not-allowed' } }

/**
 * RN68: one chip of the previous/next groups. A chip that points at a node is clickable and selects it
 * (RN62/RN63); "Start of process", "End of process" and an unresolved link are declarations, not
 * destinations, so they carry no handler at all. Filled, in the window's own green.
 */
const LinkChip = ({ testId, label, branch, onClick }) => (
  <Chip
    data-testid={testId}
    data-branch={branch}
    label={label}
    title={label}
    variant={onClick ? 'filled' : 'outlined'}
    color={branch === BRANCH.FAIL ? 'error' : onClick ? 'primary' : 'default'}
    onClick={onClick}
    disabled={!onClick}
    sx={{ height: 30, fontSize: 13, fontWeight: 600, maxWidth: '100%', '& .MuiChip-label': { px: 1.5 } }}
  />
)

/** The sets the selection lives inside, outermost first, printed above the identity. */
const Trail = ({ steps = [] }) => {
  if (steps.length === 0) {
    return null
  }
  return (
    <Box data-testid='inspector-trail' sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '12px', color: T.textFaint, ...TABULAR }}>
      {steps.map((step, index) => (
        <React.Fragment key={step + index}>
          <span>{step}</span>
          <Box component='span' sx={{ color: T.borderSoft }}>›</Box>
        </React.Fragment>
      ))}
    </Box>
  )
}

/** A card that names an operation and selects it when clicked (the content of a set, of a branch). */
/**
 * `selected` la marca como la que se esta mirando: filo a la izquierda y fondo tenue, los mismos dos
 * recursos con los que la fila seleccionada del arbol se distingue. `aria-current` para que quien no ve
 * el color tambien sepa cual es (PO, 2026-09-22).
 */
const OperationCard = ({ testId, order, title, subtitle, figure = '', onClick, selected = false }) => (
  <Box
    component='button'
    type='button'
    data-testid={testId}
    data-selected={selected}
    aria-current={selected ? 'true' : undefined}
    onClick={onClick}
    sx={{
      ...CARD_SX,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      color: 'inherit',
      font: 'inherit',
      ...(selected
        ? { borderColor: P.primary.main, background: alpha(P.primary.main, 0.14), boxShadow: '0 0 0 1px ' + P.primary.main }
        : {}),
      '&:hover': { borderColor: T.borderSoft }
    }}
  >
    <OrderBadge value={order} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ fontSize: '14.5px', fontWeight: 700, color: T.textMain, ...ELLIPSIS }}>{title}</Box>
      <Box sx={{ fontSize: '12.5px', color: T.textFaint, ...ELLIPSIS }}>{subtitle}</Box>
    </Box>
    {figure !== '' ? <Box component='span' sx={{ fontSize: '14.5px', fontWeight: 700, ...TABULAR, color: T.accent }}>{figure}</Box> : null}
  </Box>
)

/** The foot of the inspector: the process links, Previous on the left and Next on the right. */
const LinksFoot = ({ previousLabel, nextLabel, previous, next }) => (
  <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '12px 16px', borderTop: '1px solid ' + T.borderSoft, background: alpha(P.primary.main, 0.08), fontSize: '12.5px', color: T.textMuted, lineHeight: 1.45 }}>
    <Box data-testid='inspector-previous' sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ ...FIELD_LABEL, marginBottom: '6px' }}>{previousLabel}</Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{previous}</Box>
    </Box>
    <Box data-testid='inspector-next' sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ ...FIELD_LABEL, marginBottom: '6px' }}>{nextLabel}</Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{next}</Box>
    </Box>
  </Box>
)

/** The Studio's tab strip: buttons with an accent underline under the active one. */
const TabStrip = ({ tabs, active, onChange, idPrefix = 'inspector' }) => (
  <Box sx={{ display: 'flex', minHeight: '42px', padding: '0 8px', borderBottom: '1px solid ' + T.borderSoft, overflowX: 'auto', flex: '0 0 auto', marginTop: '8px' }}>
    {tabs.map((entry) => {
      const on = entry.value === active
      return (
        <Box
          component='button'
          type='button'
          key={entry.value}
          data-testid={idPrefix + '-tab-' + entry.value}
          aria-pressed={on}
          title={entry.label}
          onClick={() => onChange(entry.value)}
          sx={{ flex: '0 0 auto', minHeight: '42px', minWidth: 0, padding: '0 12px', fontSize: '12.5px', fontWeight: 700, fontFamily: 'inherit', letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', border: 'none', background: 'transparent', color: on ? T.accent : T.textFaint, cursor: 'pointer', position: 'relative', transition: 'color .15s ease' }}
        >
          {entry.label}
          <Box component='span' sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '2px', borderRadius: '2px', background: on ? T.accent : 'transparent' }} />
        </Box>
      )
    })}
  </Box>
)

/** A quiet line that explains why the surface does not edit right now. */
const Hint = ({ testId, text }) => (
  <Box data-testid={testId} sx={{ fontSize: '13px', color: T.textFaint, padding: '8px 2px', marginBottom: '4px' }}>{text}</Box>
)

const TAB = { GENERAL: 'general', TASKS: 'tasks', MATERIAL: 'materials' }

export {
  DASH, T, R, P, TABULAR, ELLIPSIS, FIELD_LABEL, FIELD_LABEL_ELLIPSIS, CARD_SX, INLINE_INPUT, SMALL_ACTION_SX, TAB,
  SkippedMark, SectionTitle, Tile, Field, OrderBadge, CardChip, EmptyCard, LinkChip, Trail, OperationCard, LinksFoot, TabStrip, Hint
}
